/**
 * FileDropZone — Drag-and-drop file upload area with file list.
 * Extracted from UploadDialog for reuse in NewTransformationWizard.
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  CloudUpload,
  AttachFile,
  DeleteOutline,
} from '@mui/icons-material';

const formatFileSize = (size: number): string => {
  if (size === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const formatted = size / Math.pow(1024, index);
  return `${formatted.toFixed(formatted >= 10 ? 0 : 1)} ${units[index]}`;
};

export const buildFileSignature = (file: File): string =>
  `${file.name}::${file.size}::${file.lastModified}`;

interface FileDropZoneProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  accept?: string;
  acceptLabel?: string;
}

const FileDropZone: React.FC<FileDropZoneProps> = ({ files, onFilesChange, accept = '.csv,.xlsx,.xls,.json,.parquet', acceptLabel = 'CSV, Excel (.xlsx), JSON, Parquet' }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const appendFiles = useCallback(
    (incoming: FileList | File[]) => {
      const next = Array.from(incoming ?? []);
      if (!next.length) return;
      const seen = new Set(files.map(buildFileSignature));
      const merged = [...files];
      next.forEach((f) => {
        const sig = buildFileSignature(f);
        if (!seen.has(sig)) {
          seen.add(sig);
          merged.push(f);
        }
      });
      onFilesChange(merged);
    },
    [files, onFilesChange],
  );

  const removeFile = useCallback(
    (signature: string) => {
      onFilesChange(files.filter((f) => buildFileSignature(f) !== signature));
    },
    [files, onFilesChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (e.dataTransfer.files?.length) {
        appendFiles(e.dataTransfer.files);
      }
    },
    [appendFiles],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept={accept}
        onChange={(e) => {
          if (e.target.files) appendFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: (theme) =>
            dragOver ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.light',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
          },
        }}
      >
        <CloudUpload
          sx={{ fontSize: 40, color: dragOver ? 'primary.main' : 'text.disabled', mb: 1 }}
        />
        <Typography variant="body1" fontWeight={500}>
          {dragOver ? 'Drop files here' : 'Drag files here or click to browse'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {acceptLabel}
        </Typography>
      </Box>

      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
            <List dense disablePadding>
              {files.map((file, index) => {
                const sig = buildFileSignature(file);
                return (
                  <React.Fragment key={sig}>
                    <ListItem sx={{ alignItems: 'flex-start', pr: 6 }}>
                      <ListItemAvatar sx={{ mt: 0.25 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'primary.light',
                            color: 'primary.dark',
                            width: 32,
                            height: 32,
                          }}
                        >
                          <AttachFile sx={{ fontSize: 16 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={file.name}
                        secondary={formatFileSize(file.size)}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: 500,
                          sx: {
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical' as const,
                          },
                        }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                        sx={{ minWidth: 0 }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove">
                          <IconButton edge="end" size="small" onClick={() => removeFile(sig)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < files.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {files.length} file{files.length !== 1 ? 's' : ''} selected &middot;{' '}
            {formatFileSize(totalSize)}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default FileDropZone;
export { formatFileSize };
